import { Outlet, createRootRouteWithContext } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { FormattedMessage } from "react-intl";

import { ErrorView } from "@shared/components/common/ErrorView";
import { LoadingShell } from "@shared/components/common/LoadingShell";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  pendingComponent: LoadingShell,
  notFoundComponent: NotFoundView,
  errorComponent: RouteErrorView,
});

function RootLayout() {
  return <Outlet />;
}

function NotFoundView() {
  return (
    <ErrorView
      title={<FormattedMessage id="error.notFound.title" defaultMessage="Page not found" />}
      message={
        <FormattedMessage
          id="error.notFound.message"
          defaultMessage="That link doesn't lead anywhere here."
        />
      }
    />
  );
}

/**
 * Route-level error boundary fallback.
 *
 * `reset` is passed by TanStack Router and re-attempts the failed route
 * load. We deliberately do not render `error.message` or any stack — those
 * are leaky in production. Logged to console for dev visibility.
 */
function RouteErrorView({ error, reset }: { error: Error; reset: () => void }) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.error("[route error]", error);
  }
  return <ErrorView onRetry={reset} />;
}
